import { useEffect, useState } from "react";
import { createLocation, updateLocation } from "../../api/locations";
import { ILocation } from "../../models/ILocation";
import { Alert, Button, Dialog, Field, Input } from "../../ui";
import "./styles.scss";

type LocationDialogProps = {
  location?: ILocation;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const LocationDialog = (props: LocationDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    googleMapsLink: "",
  });

  useEffect(() => {
    if (props.isOpen) {
      if (props.location) {
        setFormData({
          name: props.location.name,
          googleMapsLink: props.location.googleMapsLink || "",
        });
      } else {
        setFormData({
          name: "",
          googleMapsLink: "",
        });
      }
    }
  }, [props.isOpen, props.location]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setErrorMessage("Location name is required");
      return;
    }

    if (formData.name.length > 200) {
      setErrorMessage("Location name must be less than 200 characters");
      return;
    }

    if (formData.googleMapsLink && formData.googleMapsLink.length > 500) {
      setErrorMessage("Google Maps link must be less than 500 characters");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const payload = {
        name: formData.name.trim(),
        googleMapsLink: formData.googleMapsLink.trim() || undefined,
      };

      if (props.location) {
        // Update existing location
        await updateLocation(props.location.id, {
          name: payload.name,
          googleMapsLink: payload.googleMapsLink || null,
        });
      } else {
        // Create new location
        await createLocation(payload);
      }
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title={props.location ? "Edit Location" : "Create Location"}
      busy={loading}
      bodyClassName="location-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {props.location ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Field label="Location Name" required>
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="e.g., The Venue by The Clash"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={loading}
            maxLength={200}
          />
        )}
      </Field>

      <Field label="Google Maps Link (Optional)">
        {(id) => (
          <Input
            id={id}
            type="url"
            placeholder="https://maps.app.goo.gl/..."
            value={formData.googleMapsLink}
            onChange={(e) => handleChange("googleMapsLink", e.target.value)}
            disabled={loading}
            maxLength={500}
          />
        )}
      </Field>
    </Dialog>
  );
};

export default LocationDialog;
